import React, { forwardRef, ReactNode, useRef } from "react";
// import HCaptcha from "@hcaptcha/react-hcaptcha";
export type FormErrorState = Record<string, string[] | undefined> | null;
/* jscpd:ignore-start */
import { customSanitize } from "@/utils/customSanitize";
import {
  ListPropsV2,
  ListPropsV2NumbersModeValues,
  ListPropsV2StringModeValues,
  ListV2,
} from "@/components/Listbox/Listbox";

import styles from "../Form.module.scss";
import CustomSVG from "@/components/_primitives/CustomSVG/CustomSVG.client";
import { FileUploadConfig } from "../createFormStructure";

type CommonProps = {
  label?: string;
  errors?: FormErrorState;
  required_label?: string;
};

type InputProps = {
  children?: React.ReactNode;
  subline?: string;
  inputType:
    | "number"
    | "text"
    | "checkbox"
    | "tel"
    | "url"
    | "email"
    | "textarea"
    | "date";
  label_checkbox?: string;
  required_label?: string;
  checked?: boolean;
} & CommonProps &
  React.InputHTMLAttributes<HTMLInputElement>;

interface LabelProps {
  Tag?: "label" | "p" | "legend";
  label?: string;
  required: boolean | undefined;
  required_label?: string;
  htmlFor?: string;
}

export const LabelV2 = ({
  label,
  required,
  required_label,
  htmlFor,
  Tag = "label",
}: LabelProps) => {
  return (
    <Tag className={styles["label"] + " label"} htmlFor={htmlFor}>
      {label && label}
      {required && required_label && (
        <span className={styles["required-text"] + " required-text"}>
          {" "}
          ({required_label})
        </span>
      )}
    </Tag>
  );
};

// TODO: extend this component to support more input types
// This component is currently used for inputs with type: text, checkbox, email, textarea
export const InputV2 = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputProps
>(
  (
    {
      errors,
      label,
      subline,
      inputType,
      required_label,
      required,
      className = "",
      ...props
    },
    ref,
  ) => {
    const id = `input-${props.name}`;
    return (
      <FieldWrapper
        errors={errors}
        name={props.name}
        className={`w-full ${className} ${styles["input-group-formbuilder"]}`}
      >
        <LabelV2
          label={label}
          required={required}
          required_label={required_label}
          htmlFor={id}
        />
        {(inputType === "text" ||
          inputType === "email" ||
          inputType === "number" ||
          inputType === "tel" ||
          inputType === "url") && (
          <input
            id={id}
            {...props}
            type={inputType}
            ref={ref as React.Ref<HTMLInputElement>}
            className={`${styles["input"]} placeholder`}
            required={required}
          />
        )}

        {inputType === "date" && (
          <div className={styles["date-input-wrapper"]}>
            <input
              id={id}
              {...props}
              type={inputType}
              ref={ref as React.Ref<HTMLInputElement>}
              className={`${styles["input"]} ${styles["date-input"]} placeholder`}
              required={required}
            />
            <CustomSVG
              iconKey="calendar"
              className={styles["date-input-icon"]}
              aria-hidden="true"
              width="20"
              height="20"
            />
          </div>
        )}

        {subline && <span className={styles["subline"]}>{subline}</span>}
      </FieldWrapper>
    );
  },
);

InputV2.displayName = "InputV2";

type TextAriaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  CommonProps;

export const TextAreaV2 = ({
  errors,
  label,
  required_label,
  required,
  className = "",
  ...props
}: TextAriaProps) => {
  const id = `input-${props.name}`;

  return (
    <FieldWrapper
      errors={errors}
      name={props.name}
      className={`w-full ${className}`}
    >
      <LabelV2
        label={label}
        required={required}
        required_label={required_label}
        htmlFor={id}
      />
      <textarea
        {...props}
        id={id}
        className={`${styles["input"]} placeholder`}
        required={required}
      />{" "}
      <span className={styles["bar-text-area"]}></span>
    </FieldWrapper>
  );
};

export const CheckboxV2 = forwardRef<
  HTMLInputElement,
  InputProps & { big?: boolean }
>(
  (
    {
      errors,
      label,
      required_label,
      required,
      label_checkbox,
      className = "",
      inputType: _ /* otherwise it gets passed onto the input */,
      checked,
      big,
      ...props
    },
    ref,
  ) => {
    const id = `input-` + props.name;
    const checkedProps = typeof checked === "boolean" ? { checked } : {};

    return (
      <FieldWrapper
        errors={errors}
        name={props.name}
        className={`${className}`}
        inputWrapperClassName={`checkbox-wrapper`}
      >
        <fieldset>
          <LabelV2
            Tag="legend"
            label={label}
            required={required}
            required_label={required_label}
          />
          <div className={"input-checkbox"} data-is-big={!!big}>
            <input
              {...props}
              {...checkedProps}
              ref={ref as React.Ref<HTMLInputElement>}
              type="checkbox"
              id={id}
              required={required}
            />
            {label_checkbox && (
              /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
              <label
                dangerouslySetInnerHTML={{
                  __html: customSanitize(label_checkbox),
                }}
                htmlFor={id}
                className="mb-0 pb-0"
              />
            )}
          </div>
        </fieldset>
      </FieldWrapper>
    );
  },
);

CheckboxV2.displayName = "CheckboxV2";

type RadioProps = React.InputHTMLAttributes<HTMLInputElement> &
  CommonProps & {
    options: {
      label: string;
      value: string;
      checked?: boolean;
    }[];
  };

export const RadioV2 = ({
  options,
  errors,
  label,
  className,
  required_label,
  required,
  ...props
}: RadioProps) => {
  return (
    <FieldWrapper errors={errors} name={props.name} className={`${className}`}>
      {props.name === "highlight" ? (
        <legend>{label}</legend>
      ) : (
        <LabelV2
          Tag="p"
          label={label}
          required={required}
          required_label={required_label}
        />
      )}

      <div className={"input-radio"}>
        {options.map((o) => {
          const radioId = `${props.name}-${o.value}`;

          return (
            <div className="is-flex is-align-items-center mb-2" key={radioId}>
              <input
                {...props}
                checked={o.checked}
                onChange={(e) => {
                  props.onChange?.(e);
                }}
                type="radio"
                id={radioId}
                value={o.value}
              />
              <label
                className={`${styles["label"]} has-text-font-regular mb-0 pb-0`}
                htmlFor={radioId}
              >
                {o.label}
              </label>
            </div>
          );
        })}
      </div>
    </FieldWrapper>
  );
};

RadioV2.displayName = "RadioV2";

type SelectProps = CommonProps &
  Omit<React.InputHTMLAttributes<HTMLSelectElement>, "selected"> & {
    errors?: FormErrorState;
    children?: React.ReactNode;
    items: ListPropsV2["items"];

    placeholder?: string;
  } & (ListPropsV2NumbersModeValues | ListPropsV2StringModeValues);

export const SelectV2 = ({
  className,
  errors,
  items,
  required_label,
  required,
  placeholder,
  label,
  ...props
}: SelectProps) => {
  const id = "select-" + props.name;
  return (
    <FieldWrapper errors={errors} name={props.name} className={`${className}`}>
      <LabelV2
        htmlFor={id}
        label={label}
        required={required}
        required_label={required_label}
      />
      <ListV2
        className="w-full input-dropdown"
        buttonId={id}
        items={items}
        placeholder={placeholder}
        {...(props.numbersForOption
          ? {
              numbersForOption: props.numbersForOption,
              selected: props.selected,
              setSelected: props.setSelected,
            }
          : {
              numbersForOption: props.numbersForOption,
              selected: props.selected,
              setSelected: props.setSelected,
            })}
      />
    </FieldWrapper>
  );
};

interface FieldWrapperProps {
  errors: FormErrorState | undefined;
  name: string | undefined;
  children: ReactNode;
  className?: string;
  inputWrapperClassName?: string;
}

export const FieldWrapper = ({
  errors,
  name,
  children,
  className,
  inputWrapperClassName = "",
}: FieldWrapperProps) => {
  const hasError = errors && name && errors[name];

  return (
    <div
      data-field-name={name}
      className={`${styles["input"]} ${hasError ? "has-error" : ""} ${className}`}
    >
      <div className={`input-wrapper ${inputWrapperClassName}`}>{children}</div>
      {hasError && (
        <p className={"error"}>
          {(errors?.[name!]?.filter(Boolean) ?? []).join(", ")}
        </p>
      )}
    </div>
  );
};
/* jscpd:ignore-end */

type FileUploadProps = CommonProps &
  FileUploadConfig & {
    name: string;
    className?: string;
    required?: boolean;
    disabled?: boolean;
    required_label?: string;
    file: File | null;
    onFileChange: (file: File | null) => void;
  };

export const FileUploadV2 = ({
  errors,
  label,
  required_label,
  required,
  className = "",
  name,
  disabled,
  labelUploadButton,
  ariaLabelUploadButton,
  imageUploadButton,
  labelRemoveButton: _labelRemoveButton,
  ariaLabelRemoveButton,
  imageRemoveButton,
  file,
  allowedFileTypes,
  onFileChange,
}: FileUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = `input-${name}`;

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] ?? null;
    onFileChange(selectedFile);
  };

  const handleRemove = () => {
    onFileChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const mapAllowedFileTypesToAccept = (allowed?: string) => {
    if (!allowed) return undefined;

    return allowed
      .split(",")
      .map((type) => type.trim().toLowerCase())
      .filter(Boolean)
      .map((type) => {
        if (type.includes("/") || type === "*") return type;

        return `.${type}`;
      })
      .join(",");
  };
  return (
    <FieldWrapper errors={errors} name={name} className={className}>
      <LabelV2
        label={label}
        required={required}
        required_label={required_label}
        htmlFor={id}
      />
      <div className={styles["file-upload-wrapper"] + " file-upload-wrapper"}>
        <button
          type="button"
          className={styles["file-upload-button"] + " file-upload-button"}
          onClick={handleUploadClick}
          aria-label={ariaLabelUploadButton}
          disabled={disabled}
        >
          <span className={styles["file-upload-icon"] + " file-upload-icon"}>
            {imageUploadButton ? (
              <CustomSVG
                src={imageUploadButton.url}
                width={27}
                height={27}
                aria-hidden="true"
              />
            ) : (
              <svg
                width="11"
                height="15"
                viewBox="0 0 11 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M5.5 13V2M5.5 2L1 6.5M5.5 2L10 6.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          <span className={styles["file-upload-label"]}>
            {file ? file.name : labelUploadButton}
          </span>
        </button>

        {file && (
          <button
            type="button"
            className={styles["file-remove-button"] + " file-remove-button"}
            onClick={handleRemove}
            aria-label={ariaLabelRemoveButton}
            disabled={disabled}
          >
            {imageRemoveButton ? (
              <CustomSVG
                src={imageRemoveButton.url}
                width={27}
                height={27}
                aria-hidden="true"
              />
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        name={name}
        accept={mapAllowedFileTypesToAccept(allowedFileTypes)}
        onChange={handleFileSelected}
        className={"sr-only"}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />
    </FieldWrapper>
  );
};

FileUploadV2.displayName = "FileUploadV2";
